import { ChevronDown, ExternalLink, HelpCircle } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 font-medium text-brand-600 hover:underline">
      {children}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function B({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-fg">{children}</strong>;
}

type Steps = { title: string; steps: ReactNode[]; notes?: ReactNode[] };

const APPLE: Record<"en" | "fr", Steps> = {
  en: {
    title: "How to create an App Store Connect API key",
    steps: [
      <>
        Open <A href="https://appstoreconnect.apple.com/access/integrations/api">App Store Connect → Users and Access → Integrations → App Store Connect API</A>, tab <B>Team Keys</B>. The first time, the
        Account Holder must click <B>Request Access</B>.
      </>,
      <>
        Click <B>+</B> (Generate API Key), name it <B>Shipyard</B> and choose the <B>App Manager</B> access (needed to upload, manage TestFlight and submit for review).
      </>,
      <>
        <B>Download</B> the <code>AuthKey_XXXXXXXXXX.p8</code> file. Apple lets you download it <B>only once</B>.
      </>,
      <>
        Copy the <B>Key ID</B> (key row) and the <B>Issuer ID</B> (shown above the keys table) into this form, then pick the <code>.p8</code> file.
      </>,
      <>
        For each app, the <B>App Store Connect app ID</B> is the numeric <B>Apple ID</B> shown in App Store Connect → your app → App Information.
      </>,
    ],
    notes: [<>Individual keys (Users and Access → your profile) also work: choose “Individual”, there is no Issuer ID.</>],
  },
  fr: {
    title: "Créer une clé API App Store Connect",
    steps: [
      <>
        Ouvrez <A href="https://appstoreconnect.apple.com/access/integrations/api">App Store Connect → Utilisateurs et accès → Intégrations → API App Store Connect</A>, onglet <B>Clés d'équipe</B>. La
        première fois, le détenteur du compte doit cliquer sur <B>Demander l'accès</B>.
      </>,
      <>
        Cliquez sur <B>+</B> (Générer une clé API), nommez-la <B>Shipyard</B> et choisissez l'accès <B>Gestionnaire d'apps</B> (nécessaire pour envoyer des builds, gérer TestFlight et soumettre à la review).
      </>,
      <>
        <B>Téléchargez</B> le fichier <code>AuthKey_XXXXXXXXXX.p8</code>. Apple ne permet de le télécharger <B>qu'une seule fois</B>.
      </>,
      <>
        Reportez le <B>Key ID</B> (ligne de la clé) et l'<B>Issuer ID</B> (affiché au-dessus du tableau des clés) dans ce formulaire, puis choisissez le fichier <code>.p8</code>.
      </>,
      <>
        Pour chaque app, l'<B>ID App Store Connect</B> est l'<B>identifiant Apple</B> numérique visible dans App Store Connect → votre app → Informations sur l'app.
      </>,
    ],
    notes: [<>Les clés individuelles (Utilisateurs et accès → votre profil) fonctionnent aussi : choisissez « Individuelle », il n'y a pas d'Issuer ID.</>],
  },
};

const GOOGLE: Record<"en" | "fr", Steps> = {
  en: {
    title: "How to create a Google Play service account",
    steps: [
      <>
        In <A href="https://console.cloud.google.com/projectcreate">Google Cloud Console</A>, pick or create a project, then enable the{" "}
        <A href="https://console.cloud.google.com/apis/library/androidpublisher.googleapis.com">Google Play Android Developer API</A>.
      </>,
      <>
        Go to <A href="https://console.cloud.google.com/iam-admin/serviceaccounts">IAM &amp; Admin → Service accounts</A> → <B>Create service account</B> (name: <B>shipyard</B>). No Google Cloud role is needed.
      </>,
      <>
        Open the account → <B>Keys</B> → <B>Add key</B> → <B>Create new key</B> → <B>JSON</B>. Keep the downloaded file: it is the value to paste here.
      </>,
      <>
        In <A href="https://play.google.com/console/users-and-permissions">Play Console → Users and permissions</A>, <B>Invite new user</B> with the service account e-mail
        (<code>…@….iam.gserviceaccount.com</code>). Under <B>App permissions</B>, add your apps with: <B>Release to production, exclude devices, and use Play App Signing</B>,{" "}
        <B>Release apps to testing tracks</B> and <B>Manage testing tracks and edit tester lists</B>. Send the invitation.
      </>,
      <>
        Link the account to an app in Shipyard, then click <B>Test</B>: Shipyard checks the key, then its access to each linked app.
      </>,
    ],
    notes: [
      <>The very first build of an app must be uploaded manually in Play Console: the API cannot publish an app that has never had a release.</>,
      <>Permission changes can take a few minutes to apply.</>,
    ],
  },
  fr: {
    title: "Créer un compte de service Google Play",
    steps: [
      <>
        Dans la <A href="https://console.cloud.google.com/projectcreate">console Google Cloud</A>, choisissez ou créez un projet, puis activez l'
        <A href="https://console.cloud.google.com/apis/library/androidpublisher.googleapis.com">API Google Play Android Developer</A>.
      </>,
      <>
        Allez dans <A href="https://console.cloud.google.com/iam-admin/serviceaccounts">IAM et administration → Comptes de service</A> → <B>Créer un compte de service</B> (nom : <B>shipyard</B>). Aucun rôle Google Cloud n'est nécessaire.
      </>,
      <>
        Ouvrez le compte → <B>Clés</B> → <B>Ajouter une clé</B> → <B>Créer une clé</B> → <B>JSON</B>. Gardez le fichier téléchargé : c'est lui qu'il faut coller ici.
      </>,
      <>
        Dans <A href="https://play.google.com/console/users-and-permissions">Play Console → Utilisateurs et autorisations</A>, <B>Inviter un utilisateur</B> avec l'e-mail du compte de service
        (<code>…@….iam.gserviceaccount.com</code>). Dans <B>Autorisations des applications</B>, ajoutez vos apps avec : <B>Publier en production, exclure des appareils et utiliser la signature d'application Play</B>,{" "}
        <B>Publier des applications sur des canaux de test</B> et <B>Gérer les canaux de test et modifier les listes de testeurs</B>. Envoyez l'invitation.
      </>,
      <>
        Associez le compte à une app dans Shipyard, puis cliquez sur <B>Tester</B> : Shipyard vérifie la clé, puis son accès à chaque app associée.
      </>,
    ],
    notes: [
      <>La toute première build d'une app doit être envoyée à la main dans Play Console : l'API ne peut pas publier une app qui n'a jamais eu de release.</>,
      <>Les changements d'autorisations peuvent mettre quelques minutes à s'appliquer.</>,
    ],
  },
};

export function Guide({ kind, defaultOpen }: { kind: "apple" | "google"; defaultOpen?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(!!defaultOpen);
  const lang = i18n.language === "fr" ? "fr" : "en";
  const guide = (kind === "apple" ? APPLE : GOOGLE)[lang];
  return (
    <div className="rounded-md border bg-muted/40">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium">
        <span className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-brand-600" />
          {guide.title}
        </span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="space-y-3 border-t px-3 py-3 text-sm text-fg-muted">
          <ol className="list-decimal space-y-2 pl-5">
            {guide.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
          {guide.notes?.map((n, i) => (
            <p key={i} className="text-xs">
              ⓘ {n}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
